/**
 * @file   QuickStatsStrip.js
 * @brief  Three inline stat pills for Match tab: Active Matches, Peak Demand, Most Demand range.
 *         Only renders for Tier 1-2 (3+ matches).
 */
import React, { useMemo } from "react";
import { formatCompactPrice, computeSweetSpotRange } from "../../../helpers/priceCapUtils";
import "./QuickStatsStrip/quick_stats_strip.scss";

const NUM_BINS = 12;

const QuickStatsStrip = ({ matchCount, prices, adType, rangeMin, rangeMax }) => {
  // Peak demand price, computed with the SAME [rangeMin, rangeMax] binning as
  // DemandHistogram so the stat always matches the tallest bar on the chart.
  // Hooks must run unconditionally, so this stays above the early return below.
  const peakPrice = useMemo(() => {
    const span = rangeMax - rangeMin;
    if (!prices || prices.length < 3 || span <= 0) return null;
    const binWidth = span / NUM_BINS;
    const bins = new Array(NUM_BINS).fill(0);
    prices.forEach((price) => {
      let idx = Math.floor((price - rangeMin) / binWidth);
      if (idx >= NUM_BINS) idx = NUM_BINS - 1;
      if (idx < 0) idx = 0;
      bins[idx]++;
    });
    let peakIdx = 0;
    for (let i = 1; i < NUM_BINS; i++) {
      if (bins[i] > bins[peakIdx]) peakIdx = i;
    }
    return bins[peakIdx] > 0 ? rangeMin + (peakIdx + 0.5) * binWidth : null;
  }, [prices, rangeMin, rangeMax]);

  const sweetSpot = useMemo(
    () => computeSweetSpotRange(prices, adType),
    [prices, adType],
  );

  // Needs 3+ data points to be meaningful. Guard runs AFTER the hooks above so
  // the hook count stays constant across renders (React rules of hooks).
  if (!prices || prices.length < 3) return null;

  // Use server-side totalCount for Active Buyers (not prices.length which is capped by page size)
  const activeBuyerCount = matchCount || prices.length;

  return (
    <div className="quick-stats-strip" data-tour="quick-stats">
      <div className="quick-stats-strip__pill">
        <span className="quick-stats-strip__value">{activeBuyerCount}</span>
        <span className="quick-stats-strip__label">Active Buyers</span>
        <span className="quick-stats-strip__sublabel">looking for properties like yours</span>
      </div>
      {peakPrice != null && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value">{formatCompactPrice(peakPrice)}</span>
          <span className="quick-stats-strip__label">Peak Demand</span>
          <span className="quick-stats-strip__sublabel">where most buyers cluster</span>
        </div>
      )}
      {sweetSpot && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value quick-stats-strip__value--range">
            {formatCompactPrice(sweetSpot.min)} - {formatCompactPrice(sweetSpot.max)}
          </span>
          <span className="quick-stats-strip__label">Sweet Spot</span>
          <span className="quick-stats-strip__sublabel">highest concentration of demand</span>
        </div>
      )}
    </div>
  );
};

export default QuickStatsStrip;
